import type { ReactElement, ReactNode } from 'react'
import { cn } from '../../lib/cn'

/**
 * The interface icon set. Hand-drawn on a 24 grid, stroked, 1.75 weight, round joins —
 * no icon library is available and none is wanted.
 *
 * Deliberate contrast with `NetworkGlyph`: UI icons are *stroked and monochrome*, network
 * glyphs are *filled and coloured*. That is how the eye tells chrome from data at a glance.
 */
export type IconName =
  | 'dashboard'
  | 'analytics'
  | 'planner'
  | 'inbox'
  | 'brands'
  | 'settings'
  | 'billing'
  | 'admin'
  | 'search'
  | 'close'
  | 'plus'
  | 'minus'
  | 'check'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-left'
  | 'chevron-right'
  | 'arrow-up'
  | 'arrow-down'
  | 'arrow-right'
  | 'calendar'
  | 'clock'
  | 'alert'
  | 'info'
  | 'external'
  | 'upload'
  | 'download'
  | 'trash'
  | 'edit'
  | 'copy'
  | 'refresh'
  | 'filter'
  | 'sort'
  | 'user'
  | 'users'
  | 'logout'
  | 'command'
  | 'image'
  | 'eye'
  | 'link'
  | 'menu'
  | 'more'
  | 'lock'
  | 'star'
  | 'sparkle'
  | 'globe'
  | 'palette'
  | 'shield'
  | 'sun'
  | 'play'
  | 'pause'
  | 'bell'
  | 'megaphone'

const PATHS: Record<IconName, ReactNode> = {
  dashboard: (
    <>
      <rect x="3" y="3" width="7.5" height="8.5" rx="1.5" />
      <rect x="13.5" y="3" width="7.5" height="5" rx="1.5" />
      <rect x="3" y="14.5" width="7.5" height="6.5" rx="1.5" />
      <rect x="13.5" y="11" width="7.5" height="10" rx="1.5" />
    </>
  ),
  analytics: (
    <>
      <path d="M3.5 20h17" />
      <path d="M6.5 20v-6" />
      <path d="M11 20V7.5" />
      <path d="M15.5 20v-9" />
      <path d="M20 20V4.5" />
    </>
  ),
  planner: (
    <>
      <rect x="3.25" y="4.75" width="17.5" height="16" rx="2" />
      <path d="M3.25 9.5h17.5" />
      <path d="M8 3v3.5" />
      <path d="M16 3v3.5" />
      <path d="M7.5 13.5h3" />
      <path d="M13.5 13.5h3" />
      <path d="M7.5 17h3" />
    </>
  ),
  inbox: (
    <>
      <path d="M3.25 12.5h4l1.5 2.5h6.5l1.5-2.5h4" />
      <path d="M5.5 4.5h13l2.25 8v5.5a2 2 0 0 1-2 2h-15.5a2 2 0 0 1-2-2V12.5z" />
    </>
  ),
  brands: (
    <>
      <path d="M12 3.5 3.5 7.75 12 12l8.5-4.25z" />
      <path d="m3.5 12 8.5 4.25L20.5 12" />
      <path d="m3.5 16.25 8.5 4.25 8.5-4.25" />
    </>
  ),
  settings: (
    <>
      <path d="M4 7h9" />
      <path d="M17.5 7H20" />
      <path d="M4 17h3.5" />
      <path d="M12 17h8" />
      <circle cx="15.25" cy="7" r="2.25" />
      <circle cx="9.75" cy="17" r="2.25" />
    </>
  ),
  billing: (
    <>
      <rect x="2.75" y="5" width="18.5" height="14" rx="2.25" />
      <path d="M2.75 10h18.5" />
      <path d="M6.5 14.75h3.5" />
    </>
  ),
  admin: (
    <>
      <path d="M12 3.25 4.75 6.1v5.4c0 4.36 3 8.05 7.25 9.25 4.25-1.2 7.25-4.89 7.25-9.25V6.1z" />
      <path d="m9 12.1 2.1 2.15L15.25 10" />
    </>
  ),
  search: (
    <>
      <circle cx="10.75" cy="10.75" r="6.5" />
      <path d="m15.6 15.6 4.65 4.65" />
    </>
  ),
  close: (
    <>
      <path d="m5.75 5.75 12.5 12.5" />
      <path d="m18.25 5.75-12.5 12.5" />
    </>
  ),
  plus: (
    <>
      <path d="M12 4.75v14.5" />
      <path d="M4.75 12h14.5" />
    </>
  ),
  minus: <path d="M4.75 12h14.5" />,
  check: <path d="m4.75 12.4 4.9 4.85L19.25 7.4" />,
  'chevron-down': <path d="m6 9.5 6 6 6-6" />,
  'chevron-up': <path d="m6 14.5 6-6 6 6" />,
  'chevron-left': <path d="m14.5 6-6 6 6 6" />,
  'chevron-right': <path d="m9.5 6 6 6-6 6" />,
  'arrow-up': (
    <>
      <path d="M12 19.25V4.75" />
      <path d="m5.75 11 6.25-6.25L18.25 11" />
    </>
  ),
  'arrow-down': (
    <>
      <path d="M12 4.75v14.5" />
      <path d="m5.75 13 6.25 6.25L18.25 13" />
    </>
  ),
  'arrow-right': (
    <>
      <path d="M4.75 12h14.5" />
      <path d="m13 5.75 6.25 6.25L13 18.25" />
    </>
  ),
  calendar: (
    <>
      <rect x="3.25" y="4.75" width="17.5" height="16" rx="2" />
      <path d="M3.25 9.5h17.5" />
      <path d="M8 3v3.5" />
      <path d="M16 3v3.5" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 7.25V12l3.25 2" />
    </>
  ),
  alert: (
    <>
      <path d="M12 4.25 2.75 20.25h18.5z" />
      <path d="M12 10v4.25" />
      <path d="M12 17.4h.01" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M12 11v5.25" />
      <path d="M12 7.85h.01" />
    </>
  ),
  external: (
    <>
      <path d="M13.75 4.75h5.5v5.5" />
      <path d="m19.25 4.75-8 8" />
      <path d="M18 14.5v3.75a2 2 0 0 1-2 2H5.75a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2H9.5" />
    </>
  ),
  upload: (
    <>
      <path d="M12 15.5V4.75" />
      <path d="m7.5 9.25 4.5-4.5 4.5 4.5" />
      <path d="M4.75 15.5v2.75a2 2 0 0 0 2 2h10.5a2 2 0 0 0 2-2V15.5" />
    </>
  ),
  download: (
    <>
      <path d="M12 4.75V15.5" />
      <path d="m7.5 11 4.5 4.5 4.5-4.5" />
      <path d="M4.75 15.5v2.75a2 2 0 0 0 2 2h10.5a2 2 0 0 0 2-2V15.5" />
    </>
  ),
  trash: (
    <>
      <path d="M4.75 6.75h14.5" />
      <path d="M9.25 6.75V4.5h5.5v2.25" />
      <path d="M6.5 6.75 7.4 19.4a1.5 1.5 0 0 0 1.5 1.35h6.2a1.5 1.5 0 0 0 1.5-1.35l.9-12.65" />
      <path d="M10.5 10.5v6.25" />
      <path d="M13.5 10.5v6.25" />
    </>
  ),
  edit: (
    <>
      <path d="M4.75 19.25h4l10-10-4-4-10 10z" />
      <path d="m14.25 5.5 4 4" />
    </>
  ),
  copy: (
    <>
      <rect x="8.75" y="8.75" width="11.5" height="11.5" rx="2" />
      <path d="M15.25 5.75v-1a1 1 0 0 0-1-1H4.75a1 1 0 0 0-1 1v9.5a1 1 0 0 0 1 1h1" />
    </>
  ),
  refresh: (
    <>
      <path d="M19.25 12a7.25 7.25 0 1 1-2.4-5.4" />
      <path d="M19.75 4.5v4h-4" />
    </>
  ),
  filter: (
    <>
      <path d="M4.25 5.75h15.5l-6 7v6l-3.5 1.75V12.75z" />
    </>
  ),
  sort: (
    <>
      <path d="M7.5 4.75v14.5" />
      <path d="m4.25 8 3.25-3.25L10.75 8" />
      <path d="M16.5 19.25V4.75" />
      <path d="m13.25 16 3.25 3.25L19.75 16" />
    </>
  ),
  user: (
    <>
      <circle cx="12" cy="8.25" r="3.75" />
      <path d="M4.75 20.25a7.25 7.25 0 0 1 14.5 0" />
    </>
  ),
  users: (
    <>
      <circle cx="9.5" cy="8.25" r="3.5" />
      <path d="M3.25 20.25a6.25 6.25 0 0 1 12.5 0" />
      <path d="M16 5.1a3.5 3.5 0 0 1 0 6.3" />
      <path d="M17.5 14.6a6.26 6.26 0 0 1 3.25 5.65" />
    </>
  ),
  logout: (
    <>
      <path d="M14.25 4.75H6.75a2 2 0 0 0-2 2v10.5a2 2 0 0 0 2 2h7.5" />
      <path d="M11 12h9.25" />
      <path d="m16.75 8.25 3.5 3.75-3.5 3.75" />
    </>
  ),
  command: (
    <path d="M18 3a3 3 0 0 0-3 3v12a3 3 0 1 0 3-3H6a3 3 0 1 0 3 3V6a3 3 0 1 0-3 3h12a3 3 0 0 0 0-6z" />
  ),
  image: (
    <>
      <rect x="3.25" y="4.75" width="17.5" height="14.5" rx="2" />
      <circle cx="8.75" cy="9.75" r="1.5" />
      <path d="m3.75 17.25 4.75-4.5 3.5 3.25 3.25-3 4.5 4.25" />
    </>
  ),
  eye: (
    <>
      <path d="M2.75 12S6 5.75 12 5.75 21.25 12 21.25 12 18 18.25 12 18.25 2.75 12 2.75 12z" />
      <circle cx="12" cy="12" r="2.75" />
    </>
  ),
  link: (
    <>
      <path d="M10.5 13.5a3.75 3.75 0 0 0 5.3 0l2.7-2.7a3.75 3.75 0 0 0-5.3-5.3l-1.4 1.4" />
      <path d="M13.5 10.5a3.75 3.75 0 0 0-5.3 0l-2.7 2.7a3.75 3.75 0 0 0 5.3 5.3l1.4-1.4" />
    </>
  ),
  menu: (
    <>
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </>
  ),
  more: (
    <>
      <circle cx="5.5" cy="12" r="1.35" />
      <circle cx="12" cy="12" r="1.35" />
      <circle cx="18.5" cy="12" r="1.35" />
    </>
  ),
  lock: (
    <>
      <rect x="4.75" y="10.25" width="14.5" height="10" rx="2" />
      <path d="M8.25 10.25V7.5a3.75 3.75 0 0 1 7.5 0v2.75" />
    </>
  ),
  star: (
    <path d="m12 4 2.45 5.15 5.55.72-4.06 3.85 1.03 5.53L12 16.55 7.03 19.25l1.03-5.53L4 9.87l5.55-.72z" />
  ),
  /* Four-pointed star plus a small companion — the assistive-writing mark. Stroked like every
     other icon here, so it reads as chrome rather than as decoration. */
  sparkle: (
    <>
      <path d="M13 3.5c.9 3.6 1.9 4.6 5.5 5.5-3.6.9-4.6 1.9-5.5 5.5-.9-3.6-1.9-4.6-5.5-5.5 3.6-.9 4.6-1.9 5.5-5.5Z" />
      <path d="M6.75 15c.45 1.8.95 2.3 2.75 2.75-1.8.45-2.3.95-2.75 2.75-.45-1.8-.95-2.3-2.75-2.75 1.8-.45 2.3-.95 2.75-2.75Z" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.25" />
      <path d="M3.75 12h16.5" />
      <path d="M12 3.75c2.2 2.4 3.3 5.2 3.3 8.25s-1.1 5.85-3.3 8.25c-2.2-2.4-3.3-5.2-3.3-8.25S9.8 6.15 12 3.75z" />
    </>
  ),
  palette: (
    <>
      <path d="M12 3.75a8.25 8.25 0 0 0 0 16.5c1.1 0 1.75-.75 1.75-1.6 0-.5-.25-.85-.5-1.2s-.5-.7-.5-1.2c0-.85.7-1.5 1.55-1.5h1.7a4.25 4.25 0 0 0 4.25-4.25c0-3.65-3.6-6.75-8.25-6.75z" />
      <circle cx="8" cy="10.5" r="1.1" />
      <circle cx="12" cy="7.75" r="1.1" />
      <circle cx="16" cy="10.5" r="1.1" />
    </>
  ),
  shield: (
    <path d="M12 3.25 4.75 6.1v5.4c0 4.36 3 8.05 7.25 9.25 4.25-1.2 7.25-4.89 7.25-9.25V6.1z" />
  ),
  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.75v2" />
      <path d="M12 19.25v2" />
      <path d="M2.75 12h2" />
      <path d="M19.25 12h2" />
      <path d="m5.4 5.4 1.45 1.45" />
      <path d="m17.15 17.15 1.45 1.45" />
      <path d="m18.6 5.4-1.45 1.45" />
      <path d="M6.85 17.15 5.4 18.6" />
    </>
  ),
  play: <path d="M8.25 5.5 18 12l-9.75 6.5z" />,
  pause: (
    <>
      <path d="M9.25 5.25v13.5" />
      <path d="M14.75 5.25v13.5" />
    </>
  ),
  /* Notifications. The one glyph in this set nobody has to be taught, which is why it is a bell
     and not an inbox tray — the badge hangs off its top-right corner and needs the silhouette to
     narrow there. */
  bell: (
    <>
      <path d="M12 3.25a5.75 5.75 0 0 0-5.75 5.75c0 3.2-.7 4.9-1.35 5.85a.75.75 0 0 0 .62 1.15h12.96a.75.75 0 0 0 .62-1.15c-.65-.95-1.35-2.65-1.35-5.85A5.75 5.75 0 0 0 12 3.25Z" />
      <path d="M9.75 19.25a2.25 2.25 0 0 0 4.5 0" />
    </>
  ),
  /* Advertising. Deliberately a megaphone rather than a coin or a chart: the Ads screen is
     about broadcasting, and a currency glyph next to a navigation label would read as billing. */
  megaphone: (
    <>
      <path d="M3.75 10.25v3.5a1.5 1.5 0 0 0 1.5 1.5h1.5l3.5 4v-14.5l-3.5 4h-1.5a1.5 1.5 0 0 0-1.5 1.5Z" />
      <path d="M14.25 8.5a4.5 4.5 0 0 1 0 7" />
      <path d="M17.25 5.75a8.5 8.5 0 0 1 0 12.5" />
    </>
  ),
}

export interface IconProps {
  name: IconName
  size?: number
  className?: string
  /** Supply when the icon is the only content and carries meaning. */
  title?: string
}

export const Icon = ({ name, size = 18, className, title }: IconProps): ReactElement => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.75}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={cn('shrink-0', className)}
    role={title ? 'img' : undefined}
    aria-hidden={title ? undefined : true}
    aria-label={title}
  >
    {title ? <title>{title}</title> : null}
    {PATHS[name]}
  </svg>
)

export const ICON_NAMES = Object.keys(PATHS) as IconName[]
